import { kioskInstallCommand } from './kioskInstall'
import { useEffect, useState } from 'react'
import {
  fetchHosts,
  forgetHost,
  renameHost,
  setHostApprovalPolicy,
} from '../../api/ws'
import { useAppStore } from '../../store/app'
import type { Host } from '../../store/types'
import { hostRoleLabels } from './hostRoles'

// Temporary CRUD view for the user's hosts. Mirrors the
// ResourceConnections debug sheet: rename inline, read-only path
// (immutable post-enroll), modal-confirm Forget. Reachable via
// gear → About → Debug → "Hosts". Two clicks deep, unloved by
// design until the long-term host UX gets a real spec.
export function HostsSheet() {
  const hosts = useAppStore((s) => s.hosts)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchHosts()
  }, [])

  function handleError(e: unknown) {
    setError(typeof e === 'string' ? e : (e as Error)?.message ?? String(e))
  }

  async function refresh() {
    fetchHosts()
  }

  return (
    <div style={{ padding: '12px 16px', fontSize: 14 }}>
      {error && (
        <div
          style={{
            padding: '8px 12px',
            background: '#fee',
            color: '#900',
            borderRadius: 4,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      {hosts.length === 0 ? (
        <p style={{ opacity: 0.6 }}>No hosts enrolled yet.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {hosts.map((h) => (
            <HostRow
              key={h.host_id}
              host={h}
              onError={handleError}
              onClearError={() => setError(null)}
              onMutated={refresh}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function HostRow({
  host,
  onError,
  onClearError,
  onMutated,
}: {
  host: Host
  onError: (e: unknown) => void
  onClearError: () => void
  onMutated: () => void
}) {
  const [hostnameDraft, setHostnameDraft] = useState(host.hostname)
  const [busy, setBusy] = useState(false)
  const humanUserID = useAppStore((s) => s.humanUserID)
  const pushSheet = useAppStore((s) => s.pushSheet)
  const isHostOwner = !!host.owner_human_user_id && host.owner_human_user_id === humanUserID
  const policy = host.approval_policy ?? 'owner_only'

  async function setPolicy(next: 'owner_only' | 'org_members') {
    if (busy || next === policy) return
    onClearError()
    setBusy(true)
    try {
      const resp = await setHostApprovalPolicy({ hostID: host.host_id, policy: next })
      if (resp?.error) {
        onError(resp.error)
        return
      }
      onMutated()
    } catch (e) {
      onError(e)
    } finally {
      setBusy(false)
    }
  }

  // Resync drafts when the host updates from a server push (rename
  // landed, etc.). Otherwise the input would silently override fresh
  // server state.
  useEffect(() => {
    setHostnameDraft(host.hostname)
  }, [host.hostname])

  const hostnameDirty = hostnameDraft.trim() !== host.hostname

  async function saveHostname() {
    if (!hostnameDirty) return
    onClearError()
    setBusy(true)
    try {
      const resp = await renameHost({
        hostID: host.host_id,
        newHostname: hostnameDraft.trim(),
      })
      if (resp?.error) {
        onError(resp.error)
        return
      }
      onMutated()
    } catch (e) {
      onError(e)
    } finally {
      setBusy(false)
    }
  }

  async function handleForget() {
    if (
      !confirm(
        `Forget "${host.hostname}"?\n\n` +
          'The host will be revoked server-side and the daemon will go ' +
          'offline immediately. The host_id stays in the database as a ' +
          'tombstone; the operator can re-enroll fresh on the same machine ' +
          '(a new host_id will be minted).',
      )
    ) {
      return
    }
    onClearError()
    setBusy(true)
    try {
      const resp = await forgetHost(host.host_id)
      if (resp?.error) {
        onError(resp.error)
        return
      }
      onMutated()
    } catch (e) {
      onError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <li
      style={{
        padding: '12px 0',
        borderBottom: '1px solid #eee',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <div style={{ fontWeight: 600 }}>{host.hostname}</div>
        <div style={{ fontSize: 12, color: host.online ? 'var(--success)' : 'var(--label-secondary)' }}>
          ● {host.online ? 'online' : 'offline'}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {hostRoleLabels(host.roles).map((label) => (
          <span
            key={label}
            style={{
              fontSize: 11,
              padding: '2px 8px',
              borderRadius: 10,
              border: '1px solid var(--separator, #ddd)',
              color: 'var(--label-secondary)',
            }}
          >
            {label}
          </span>
        ))}
      </div>

      <DisplayPairing host={host} />


      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 12, opacity: 0.7 }}>Who approves this host's agent actions</span>
        {policy === 'custom' ? (
          <div style={{ fontSize: 13 }}>
            <span style={{ opacity: 0.8 }}>Custom policy — </span>
            <button
              onClick={() => pushSheet({ kind: 'approvers' })}
              style={{ border: 'none', background: 'none', padding: 0, color: 'var(--tint, #0a84ff)', cursor: 'pointer', fontSize: 'inherit' }}
            >
              edit in Approvers
            </button>
          </div>
        ) : isHostOwner ? (
          <div
            style={{
              display: 'flex',
              width: 'fit-content',
              border: '1px solid var(--separator, #ddd)',
              borderRadius: 6,
              overflow: 'hidden',
            }}
          >
            {(['owner_only', 'org_members'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPolicy(p)}
                disabled={busy}
                style={{
                  fontSize: 13,
                  padding: '6px 12px',
                  border: 'none',
                  background: policy === p ? 'var(--tint, #0a84ff)' : 'transparent',
                  color: policy === p ? '#fff' : 'var(--label, inherit)',
                  cursor: busy ? 'default' : 'pointer',
                }}
              >
                {p === 'owner_only' ? 'Just me' : 'Anyone in the home'}
              </button>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 13, opacity: 0.8 }}>
            {policy === 'org_members' ? 'Anyone in the household' : 'Just the owner'}
          </div>
        )}
        <span style={{ fontSize: 11, opacity: 0.6 }}>
          Applies to tool actions from agents on this host. Secrets and connections stay owner-only.
          {policy !== 'custom' && isHostOwner && (
            <>
              {' · '}
              <button
                onClick={() => pushSheet({ kind: 'approvers' })}
                style={{ border: 'none', background: 'none', padding: 0, color: 'var(--tint, #0a84ff)', cursor: 'pointer', fontSize: 'inherit' }}
              >
                Advanced…
              </button>
            </>
          )}
        </span>
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 12, opacity: 0.7 }}>Rename</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={hostnameDraft}
            onChange={(e) => setHostnameDraft(e.target.value)}
            style={{ flex: 1, fontSize: 16 }}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <button onClick={saveHostname} disabled={busy || !hostnameDirty}>
            Save
          </button>
        </div>
      </label>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 12, opacity: 0.7 }}>Agent home path</span>
        <div
          style={{
            fontSize: 14,
            fontFamily: 'monospace',
            opacity: host.agent_home_path ? 1 : 0.5,
            wordBreak: 'break-all',
          }}
        >
          {host.agent_home_path || '(not set)'}
        </div>
      </div>

      <div>
        <button
          onClick={handleForget}
          disabled={busy}
          style={{ color: '#900' }}
        >
          Forget
        </button>
      </div>
    </li>
  )
}

// DisplayPairing shows the browser URL(s) to open on a phone or wall tablet to pair a
// new screen with this household — the display server's LAN address (host_id:port).
// The daemon reports it only while serving displays, so its presence doubles as "this
// host is currently driving screens." Renders nothing for agent-only hosts. Until a
// richer Displays surface exists this lives under the host, as the one place that
// answers "what do I type into the browser to add a screen?"
function DisplayPairing({ host }: { host: Host }) {
  const urls = host.display_pairing?.urls ?? []
  const [copied, setCopied] = useState<string | null>(null)
  if (urls.length === 0) return null

  async function copy(url: string) {
    try {
      await navigator.clipboard?.writeText(url)
      setCopied(url)
      setTimeout(() => setCopied((c) => (c === url ? null : c)), 1500)
    } catch {
      /* clipboard unavailable (insecure context / denied) — the text is selectable */
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 12, opacity: 0.7 }}>Pair a display — open in a browser on the same network</span>
      {urls.map((url) => (
        <div key={url} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{ fontSize: 14, fontFamily: 'monospace', wordBreak: 'break-all', userSelect: 'all' }}
          >
            {url}
          </span>
          <button
            onClick={() => copy(url)}
            style={{
              fontSize: 11,
              padding: '2px 8px',
              border: '1px solid var(--separator, #ddd)',
              borderRadius: 6,
              background: 'transparent',
              color: 'var(--tint, #0a84ff)',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            {copied === url ? 'Copied' : 'Copy'}
          </button>
        </div>
      ))}
      <span style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
        Set up a Raspberry Pi as a wall screen — run on the Pi, as the user it logs in as
      </span>
      {urls.map((url) => {
        const cmd = kioskInstallCommand(url)
        return (
          <div key={cmd} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{ fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all', userSelect: 'all' }}
            >
              {cmd}
            </span>
            <button
              onClick={() => copy(cmd)}
              style={{
                fontSize: 11,
                padding: '2px 8px',
                border: '1px solid var(--separator, #ddd)',
                borderRadius: 6,
                background: 'transparent',
                color: 'var(--tint, #0a84ff)',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              {copied === cmd ? 'Copied' : 'Copy'}
            </button>
          </div>
        )
      })}
    </div>
  )
}
